import type { ReactNode } from 'react';

import { ChooseProductToReview } from '../chooseProductToReview/chooseProductToReview';
import { useRateAndReviewModalClose } from '../rateAndReviewModal/rateAndReviewModal';
import type { RateAndReviewModalVariantProps } from '../rateAndReviewModalVariant';
import { ReviewSubmitFailed } from '../reviewSubmitFailed/reviewSubmitFailed';
import { ReviewSubmitted } from '../reviewSubmitted/reviewSubmitted';

export type RateAndReviewPhaseBodyProps = Pick<
  RateAndReviewModalVariantProps,
  | 'errorMessage'
  | 'isRetryable'
  | 'isSubmitting'
  | 'moreToReview'
  | 'onRequestClose'
  | 'onReviewProduct'
  | 'onSubmit'
  | 'productName'
  | 'status'
  | 'value'
> & {
  /** The variant's questions, shown while `editing`. */
  form: ReactNode;
};

/** Picks the body for the current phase; every A/B test variant shares all but the form. */
export function RateAndReviewPhaseBody({
  errorMessage,
  form,
  isRetryable = true,
  isSubmitting,
  moreToReview = [],
  onRequestClose,
  onReviewProduct,
  onSubmit,
  productName,
  status = 'editing',
  value,
}: RateAndReviewPhaseBodyProps) {
  const close = useRateAndReviewModalClose(onRequestClose);

  switch (status) {
    case 'choosing':
      return (
        <ChooseProductToReview
          moreToReview={moreToReview}
          onDone={close}
          onReviewProduct={onReviewProduct}
        />
      );
    case 'submitted':
      return (
        <ReviewSubmitted
          moreToReview={moreToReview}
          onDone={close}
          onReviewProduct={onReviewProduct}
          productName={productName}
          rating={value.rating}
        />
      );
    case 'failed':
      return isRetryable ? (
        <ReviewSubmitFailed
          errorMessage={errorMessage}
          isSubmitting={isSubmitting}
          onRetry={onSubmit}
        />
      ) : (
        <ReviewSubmitFailed
          errorMessage={errorMessage}
          isRetryable={false}
          onClose={close}
        />
      );
    case 'editing':
      return form;
  }
}
